import { type ComponentType, forwardRef, InputHTMLAttributes, ReactNode, type SVGAttributes } from 'react'

import { AlertCircle, HelpCircle } from '@foxino/icons-react'

import { Tooltip } from '../Tooltip'
import resolveTailwindClasses from '../utils/resolveTailwindClasses'

type VariantType = 'outlined' | 'simple'

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  LeadingIcon?: ComponentType<SVGAttributes<SVGElement>>
  leftPart?: ReactNode
  rightPart?: ReactNode
  disabled?: boolean
  error?: boolean
  label?: JSX.Element | string
  hint?: string
  description?: JSX.Element | string
  errorText?: JSX.Element | string
  inputClassName?: string
  wrapperClassName?: string
  leadingIconClassName?: string
  variant?: VariantType
}

const variantToStyle: Record<VariantType, string> = {
  outlined: 'border focus-within:border-1 pl-3 pr-2',
  simple: ''
}

const validClasses =
  'bg-primary-1000 border-primary-700 placeholder:text-neutral-500 focus-within:border-primary-400 hover:border-primary-400'
const errorClasses = 'bg-primary-1000 border-error-500 placeholder:text-neutral-500 focus-within:border-error-500'
const disabledClasses = 'bg-primary-1000 text-primary-600 border-primary-700 placeholder:text-primary-600'

/**
 * @group Components
 * @category Component
 */
export const TextInput = forwardRef(
  (
    {
      className,
      inputClassName,
      wrapperClassName,
      leadingIconClassName,
      disabled,
      LeadingIcon,
      leftPart,
      rightPart,
      description,
      error,
      errorText,
      hint,
      label,
      type = 'text',
      inputMode = 'text',
      variant = 'outlined',
      ...restProps
    }: TextInputProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const hasError = !!errorText || error
    const variantStyle = variantToStyle[variant]

    return (
      <div className={resolveTailwindClasses('flex flex-col gap-2 text-sm text-neutral-100', className)}>
        {label ? (
          <label htmlFor={restProps.id || restProps.name} className="text-primary-200">
            {label}
          </label>
        ) : null}
        <div
          className={resolveTailwindClasses(
            'flex flex-row items-center gap-2 rounded-lg py-2',
            disabled ? disabledClasses : hasError ? errorClasses : validClasses,
            variantStyle,
            wrapperClassName
          )}
        >
          {leftPart}
          {LeadingIcon && <LeadingIcon className={resolveTailwindClasses('size-4', leadingIconClassName)} />}

          <input
            {...restProps}
            ref={ref}
            type={type}
            inputMode={inputMode}
            disabled={disabled}
            className={resolveTailwindClasses(
              'w-full flex-1 items-center rounded-md bg-transparent text-base text-primary-200 focus:outline-none',
              disabled ? 'text-primary-600' : '',
              inputClassName
            )}
          />
          {hasError ? (
            <>
              {hint ? (
                <Tooltip content={hint} side="top" isError>
                  <AlertCircle className="text-error-500" />
                </Tooltip>
              ) : (
                <AlertCircle className="text-error-500" />
              )}
            </>
          ) : hint ? (
            <Tooltip content={hint} side="top" disabled={disabled}>
              <HelpCircle className="text-neutral-500" />
            </Tooltip>
          ) : null}
          {rightPart}
        </div>
        {errorText ? (
          <div className="text-sm text-error-500">{errorText}</div>
        ) : description ? (
          <div className="text-sm text-neutral-500">{description}</div>
        ) : null}
      </div>
    )
  }
)

TextInput.displayName = 'TextInput'
